import {
  BanIcon,
  ChartColumnIcon,
  CircleCheckIcon,
  CircleHelpIcon,
  EllipsisIcon,
  LibraryBigIcon,
  LoaderCircleIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  Trash2Icon,
  XIcon,
  type LucideIcon,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import type { SessionSummary } from "../../../../shared/api.ts";
import { formatCode } from "../../../../shared/uktzed.ts";
import { Wordmark } from "@/components/brand/logo.tsx";
import { ErrorState } from "@/components/common/states.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Tooltip } from "@/components/ui/tooltip.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { isMockTransport } from "@/lib/http.ts";
import { useCurrentUser, useSessions } from "@/lib/queries.ts";
import { groupSessions, sessionCode, sessionStatus } from "@/lib/turns.ts";
import { cn } from "@/lib/utils.ts";
import { DeleteSessionDialog, RenameSessionDialog, useCreateSession } from "./session-dialogs.tsx";
import { UserMenu } from "./user-menu.tsx";

function StatusMark({ session }: { session: SessionSummary }) {
  const { t } = useI18n();
  const status = sessionStatus(session);
  if (status === "running") {
    return <LoaderCircleIcon className="size-4 shrink-0 animate-spin text-primary" aria-label={t.shell.statusRunning} />;
  }
  if (status === "classified") {
    return <CircleCheckIcon className="size-4 shrink-0 text-success" aria-label={t.shell.statusClassified} />;
  }
  if (status === "clarifying") {
    return <CircleHelpIcon className="size-4 shrink-0 text-warning" aria-label={t.shell.statusClarifying} />;
  }
  if (status === "rejected") {
    return <BanIcon className="size-4 shrink-0 text-muted-foreground" aria-label={t.shell.statusRejected} />;
  }
  return <span className="mx-[5px] size-1.5 shrink-0 rounded-full bg-border-strong" aria-hidden />;
}

function SessionRow({ session }: { session: SessionSummary }) {
  const { t } = useI18n();
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const code = sessionCode(session);
  const title = session.title || t.shell.untitled;
  return (
    <li className="group/row relative">
      <NavLink
        to={`/app/s/${session.id}`}
        className={({ isActive }) =>
          cn(
            "flex min-h-11 items-center gap-2.5 rounded-lg py-1.5 pr-9 pl-2.5 transition-colors outline-none hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-ring/35",
            isActive && "bg-sidebar-accent shadow-[inset_2px_0_0_var(--primary)]",
          )
        }
      >
        <StatusMark session={session} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] leading-5 font-medium text-foreground">{title}</span>
          {code ? <span className="code-mono block truncate text-[11.5px] leading-4 text-muted-foreground">{formatCode(code)}</span> : null}
        </span>
      </NavLink>
      <div className="absolute top-1/2 right-1 -translate-y-1/2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost-muted"
              size="icon-xs"
              aria-label={`${t.shell.sessionMenu}: ${title}`}
              className="opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
            >
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            <DropdownMenuItem onSelect={() => setRenaming(true)}>
              <PencilIcon aria-hidden />
              {t.common.rename}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="danger" onSelect={() => setDeleting(true)}>
              <Trash2Icon aria-hidden />
              {t.common.delete}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <RenameSessionDialog session={session} open={renaming} onOpenChange={setRenaming} />
      <DeleteSessionDialog session={session} open={deleting} onOpenChange={setDeleting} />
    </li>
  );
}

function matches(session: SessionSummary, query: string): boolean {
  const text = query.trim().toLocaleLowerCase();
  if (!text) return true;
  if (session.title.toLocaleLowerCase().includes(text)) return true;
  const digits = text.replace(/\D/g, "");
  if (digits.length >= 2) {
    return [session.chosenCode, session.declaredCode].some((code) => code?.includes(digits));
  }
  return [session.chosenCode, session.declaredCode].some((code) => code && formatCode(code).includes(text));
}

function SessionList({ query }: { query: string }) {
  const { t } = useI18n();
  const sessions = useSessions();
  const groups = useMemo(() => groupSessions((sessions.data ?? []).filter((session) => matches(session, query))), [sessions.data, query]);

  if (sessions.isPending) {
    return (
      <div className="flex flex-col gap-1 px-2.5 py-2" aria-hidden>
        <Skeleton className="mb-2 h-3 w-16" />
        {[78, 64, 88, 70, 58].map((width) => (
          <div key={width} className="flex items-center gap-2.5 py-2">
            <Skeleton className="size-4 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-3.5" style={{ width: `${width}%` }} />
              <Skeleton className="mt-1.5 h-2.5 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (sessions.isError) {
    return <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} className="py-6" />;
  }
  if ((sessions.data ?? []).length === 0) {
    return <p className="px-3 py-6 text-center text-[12.5px] leading-relaxed text-muted-foreground">{t.shell.noSessions}</p>;
  }
  if (groups.length === 0) {
    return <p className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">{t.shell.noMatches}</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <section key={group.key} aria-label={t.shell.groups[group.key]}>
          <h3 className="px-2.5 pt-1 pb-1 text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">{t.shell.groups[group.key]}</h3>
          <ul className="flex flex-col gap-px">
            {group.sessions.map((session) => (
              <SessionRow key={session.id} session={session} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function BottomLink({ to, icon: Icon, label, count, countLabel }: { to: string; icon: LucideIcon; label: string; count?: number; countLabel?: string }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium text-foreground/85 transition-colors outline-none hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/35",
        active && "bg-sidebar-accent text-foreground",
      )}
    >
      <Icon className="size-4 text-muted-foreground" aria-hidden />
      {label}
      {count ? (
        <Badge variant="warning" className="ml-auto h-5 min-w-5 justify-center px-1.5 code-mono text-[11px]" aria-label={countLabel}>
          {count}
        </Badge>
      ) : null}
    </Link>
  );
}

/** Logo, New classification, search, the session list, and the links at the bottom. */
export function Sidebar({ inDrawer = false }: { inDrawer?: boolean }) {
  const { t } = useI18n();
  const me = useCurrentUser();
  const create = useCreateSession();
  // Accounts on the waitlist and access requests. Polled, because without mail this is how an admin learns of them.
  const badges = useQuery({ queryKey: queryKeys.admin.badges, queryFn: api.admin.badges, enabled: me.user.role === "admin", refetchInterval: 60_000 });
  const waiting = badges.data ? badges.data.pendingTenants + badges.data.accessRequests : 0;
  const [query, setQuery] = useState("");
  return (
    <div className="flex h-full min-h-0 flex-col bg-sidebar text-sidebar-foreground">
      <div className={cn("flex h-14 shrink-0 items-center gap-2 px-4", inDrawer && "pr-12")}>
        <Link to="/app" className="rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35">
          <Wordmark name={t.brand.name} />
        </Link>
        {isMockTransport() ? (
          <Badge variant="warning" className="ml-auto">
            {t.common.demoData}
          </Badge>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col gap-2 px-3 pb-2">
        <Button className="w-full justify-start" onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending ? <LoaderCircleIcon className="animate-spin" /> : <PlusIcon />}
          {t.shell.newClassification}
        </Button>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.shell.searchPlaceholder}
            aria-label={t.shell.searchLabel}
            className="h-9 w-full rounded-lg border border-sidebar-border bg-background pr-8 pl-8.5 text-[13px] outline-none placeholder:text-muted-foreground/80 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/25 [&::-webkit-search-cancel-button]:hidden"
          />
          {query ? (
            <Tooltip content={t.common.clear}>
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label={t.common.clear}
                className="absolute top-1/2 right-1.5 flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground"
              >
                <XIcon className="size-3.5" />
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <nav aria-label={t.shell.sessionsLabel} className="min-h-0 flex-1 overflow-y-auto px-2 pt-1 pb-3">
        <SessionList query={query} />
      </nav>
      <div className="shrink-0 border-t border-sidebar-border p-2">
        <BottomLink to="/app/uktzed" icon={LibraryBigIcon} label={t.shell.dictionary} />
        <BottomLink to="/app/usage" icon={ChartColumnIcon} label={t.shell.usage} />
        <BottomLink to="/app/settings" icon={SettingsIcon} label={t.shell.settings} />
        {me.user.role === "admin" ? (
          <BottomLink to="/admin" icon={ShieldCheckIcon} label={t.shell.admin} count={waiting} countLabel={waiting ? t.shell.adminBadge(waiting) : undefined} />
        ) : null}
      </div>
      <div className="shrink-0 border-t border-sidebar-border p-2">
        <UserMenu />
      </div>
    </div>
  );
}
